import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import Sanity client and urlFor

// Consistent Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Consistent Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

const DismissalLocations = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const query = `*[_type == "dismissalLocationsPage" && _id == "dismissalLocationsPageContent"][0] {
          bannerTitle,
          bannerImage {
            asset->{
              _id,
              url
            }
          },
          pageHeading,
          dismissalBoxes[]{
            _key,
            boxTitle,
            locationDescription,
            roomNumbers
          }
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('Dismissal Locations Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch Dismissal Locations page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Dismissal Locations'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
             <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500">Banner Image Missing</span>
            </div> 
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Dismissal Locations'} {/* Dynamic */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-5xl mx-auto">
            {pageData.pageHeading && (
              <h2 className="text-2xl font-bold text-[#2a4865] mb-6 relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                {pageData.pageHeading}
              </h2>
            )}
            
            {pageData.dismissalBoxes?.length > 0 ? (
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                {pageData.dismissalBoxes.map(box => (
                  <div key={box._key} className="bg-white border border-gray-100 rounded-lg shadow-md overflow-hidden">
                    <h4 className="text-lg font-bold bg-[#2a4865] text-white p-3 flex items-center">
                      {/* Keep exact styling */} 
                      <span className="w-1 h-6 bg-yellow-500 mr-3"></span>
                      {box.boxTitle}
                    </h4>
                    <div className="p-5">
                      <p className="mb-4 font-medium text-[#691e31] flex items-center">
                        {/* Keep exact icon */} 
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {box.locationDescription}
                      </p>
                      {box.roomNumbers?.length > 0 && (
                        <ul className="grid grid-cols-2 gap-2">
                          {box.roomNumbers.map((room, index) => (
                            <li key={index} className="flex items-center">
                              {/* Keep exact icon */}
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <span>{room}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-10 text-center text-gray-500">Dismissal locations not available.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DismissalLocations; 